// @ts-nocheck
/* eslint-disable react/prop-types */
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ReactComponent as ModeEditOutlinedIcon } from '../../../../assets/Icons/lucideIcons/pencil.svg';
import { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import InfoIconButton from './InfoIconButton';
import {
  findGroupNameByGroupId,
  findUsernameByUserId,
  formatAttributeValue,
} from '../../../../utils/Utils';
import MultiEditAttributeField from '../MultiEditAttributeField';
import OverrideRationaleTooltip from '../../OverrideRationaleTooltip';
import { ReactComponent as CalculationIcon } from '../../../../assets/images/CalculationIcon.svg';
import useTextOverflow from '../../../../hooks/useTextOverFlow';
import { EMPTY_OBJECT } from '../../../../const/CommonConst';
import {
  useShowDraftChip,
  useDraftAttributeValue,
} from '../../../../hooks/useDraftAutoSave.jsx';

const USER_DATA_TYPE = 'User';
const GROUP_DATA_TYPE = 'Group';
const MULTI_SELECT_INPUT_CONTROL = 'MULTI_SELECT';

const valueCellTextSx = {
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  width: '100%',
};

/** Read-mode value: `formattedDisplayValue` from formatAttributeValue (includes HTML preview link). */
export const ValueCellAttributeValueDisplay = ({
  formattedDisplayValue,
  textRef,
  matchedOverride,
  includeOverrideTooltip,
}) => (
  <Typography ref={textRef} variant="body8" sx={valueCellTextSx}>
    {formattedDisplayValue}
    {includeOverrideTooltip && matchedOverride?.justification ? (
      <OverrideRationaleTooltip
        justification={matchedOverride?.justification}
      />
    ) : null}
  </Typography>
);

const ValueCell = ({
  row,
  value,
  onEdit,
  editPermission,
  isMultipleEditMode,
  entityDetailsFormikForm,
  usersList,
  teamUsersList,
  groupList,
  entityData,
  handleChange,
  attributeSourceMapping,
  loading,
  isTableView = true,
  isDerived,
  overrideData,
  handleOpenDerivedModal,
  handleAutoSave,
  // Constraint-related props
  lookupSourceAttributes,
  autoCompleteSourceAttributes,
  overrideDropdownOptions,
  isLoadingConstraints,
  dateCalendarConstraints,
  dateCalendarSourceAttributes,
  hasDraftChanges = false,
  groupDetails = EMPTY_OBJECT,
  mandatoryConstraints = EMPTY_OBJECT,
  hideAttributeInfo = false,
  associationTypes = EMPTY_OBJECT,
  modelTemplateDetails = EMPTY_OBJECT,
  onHistoryClick,
}) => {
  const attribute = {
    ...(row?.original || {}),
    entityType: entityData?.entity_type,
    requestType: entityData?.request_type,
    // Apply mandatory constraints if present for this attribute
    ...(mandatoryConstraints?.[row?.original?.id] !== undefined &&
      mandatoryConstraints?.[row?.original?.id] !== null && {
        is_mandatory:
          mandatoryConstraints[row?.original?.id]?.is_mandatory === true,
      }),
  };
  const { frontendConfig } = useSelector((state) => state?.tenant);
  const isEditEnabled = useMemo(
    () =>
      !isMultipleEditMode &&
      !isDerived &&
      editPermission &&
      !frontendConfig?.hideSingleEditMode,
    [
      isMultipleEditMode,
      isDerived,
      editPermission,
      frontendConfig?.hideSingleEditMode,
    ]
  );

  // Create a bound change handler for this specific attribute (always created, not conditionally)
  const attributeChangeHandler = useCallback(
    (newValue) => {
      if (attribute?.id) {
        handleChange(attribute?.id, newValue);
      }
    },
    [handleChange, attribute?.id]
  );
  const handleClickAttribute = useCallback(
    (e) => {
      e.stopPropagation();
      handleOpenDerivedModal(attribute);
    },
    [handleOpenDerivedModal, attribute]
  );
  const matchedOverride = useMemo(() => {
    return overrideData?.find?.((item) => item?.attributeId === attribute?.id);
  }, [overrideData, attribute?.id]);

  const showDraftChip = useShowDraftChip(attribute);
  const draftAttributeValue = useDraftAttributeValue(attribute?.id);

  // In multiple edit mode, use formik values; otherwise use the passed value
  const displayValue = useMemo(() => {
    let currentValue;
    if (isMultipleEditMode) {
      const formikValues = entityDetailsFormikForm?.values ?? {};
      const attrId = attribute?.id;
      const formikVal = formikValues[attrId] ?? formikValues[Number(attrId)];

      if (
        showDraftChip &&
        draftAttributeValue !== undefined &&
        (formikVal === undefined || formikVal === value)
      ) {
        currentValue = draftAttributeValue;
      } else {
        currentValue = formikVal !== undefined ? formikVal : value;
      }
    } else if (showDraftChip && draftAttributeValue !== undefined) {
      currentValue = draftAttributeValue;
    } else {
      currentValue = value;
    }

    if (
      attribute?.data_type === GROUP_DATA_TYPE &&
      attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
    ) {
      const groupIds = currentValue || [];
      const groupNames = groupIds?.map((groupId) =>
        findGroupNameByGroupId(groupId, groupList)
      );
      currentValue = groupNames?.join(', ');
    }

    if (
      attribute?.data_type === USER_DATA_TYPE &&
      attribute?.input_control_type === MULTI_SELECT_INPUT_CONTROL
    ) {
      const userIds = currentValue || [];
      const userNames = userIds?.map((userId) =>
        findUsernameByUserId(userId, usersList)
      );
      currentValue = userNames?.join(', ');
    }

    return currentValue;
  }, [
    isMultipleEditMode,
    showDraftChip,
    draftAttributeValue,
    entityDetailsFormikForm?.values,
    attribute?.id,
    attribute?.data_type,
    value,
    groupList,
    usersList,
  ]);

  // Use the centralized utility function to format the value for display
  const formattedDisplayValue = formatAttributeValue(
    displayValue,
    attribute,
    hasDraftChanges,
    entityData?.entity_id,
    entityDetailsFormikForm,
    usersList,
    groupList,
    entityData?.entity_type,
    associationTypes,
    false,
    false,
    entityData
  );
  const { ref: textRef, isOverflowed } = useTextOverflow(formattedDisplayValue);

  const isEditable = useMemo(() => attribute?.is_editable, [attribute]);

  const showCalculationIcon = useMemo(() => {
    return (
      !isMultipleEditMode &&
      isDerived &&
      editPermission &&
      attribute?.input_control_type !== 'SINGLE_SELECT' &&
      !frontendConfig?.hideSingleEditMode
    );
  }, [
    isMultipleEditMode,
    isDerived,
    editPermission,
    attribute?.input_control_type,
    frontendConfig?.hideSingleEditMode,
  ]);
  //if attribute is non-editable
  if (!isEditable && !isDerived) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          '&:hover .edit-icon-button': {
            opacity: 1,
            visibility: 'visible',
          },
        }}
      >
        <Tooltip
          title={
            typeof formattedDisplayValue !== 'object' && isOverflowed
              ? formattedDisplayValue
              : ''
          }
        >
          <ValueCellAttributeValueDisplay
            formattedDisplayValue={formattedDisplayValue}
            textRef={textRef}
            matchedOverride={matchedOverride}
            includeOverrideTooltip={false}
          />
        </Tooltip>
        {hideAttributeInfo && (
          <InfoIconButton
            attribute={attribute}
            attributeSourceMapping={attributeSourceMapping}
            loading={loading}
          />
        )}
      </Box>
    );
  }

  // If in multiple edit mode and user has edit permission, show the form field
  if (isMultipleEditMode && editPermission) {
    return (
      <Box
        sx={{
          width: '100%',
          minHeight: '40px',
          display: 'flex',
          alignItems: 'center',
          // This is for removing the label of the field
          '& .MuiFormControl-root': {
            marginTop: '0px',
            '& .MuiInputLabel-root': {
              display: 'none',
            },
          },
        }}
      >
        <Box
          sx={[
            {
              width: '100%',
              display: 'flex',
              alignItems: 'center',
            },
            !isTableView
              ? {
                  gap: 1,
                  maxWidth: '480px',
                }
              : undefined,
          ]}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
            }}
          >
            <MultiEditAttributeField
              attribute={attribute}
              value={displayValue}
              usersList={usersList}
              teamUsersList={teamUsersList}
              entityData={entityData}
              onChange={attributeChangeHandler}
              showLabel={false}
              entityDetailsFormikForm={entityDetailsFormikForm}
              handleOpenDerivedModal={handleOpenDerivedModal}
              isDerived={isDerived}
              handleAutoSave={handleAutoSave}
              // Constraint-related props
              lookupSourceAttributes={lookupSourceAttributes}
              autoCompleteSourceAttributes={autoCompleteSourceAttributes}
              overrideDropdownOptions={overrideDropdownOptions}
              isLoadingConstraints={isLoadingConstraints}
              dateCalendarConstraints={dateCalendarConstraints}
              dateCalendarSourceAttributes={dateCalendarSourceAttributes}
              groupDetails={groupDetails}
              modelTemplateDetails={modelTemplateDetails}
              onHistoryClick={onHistoryClick}
            />
          </Box>

          <Box sx={{ flexShrink: 0 }}>
            {hideAttributeInfo && (
              <InfoIconButton
                attribute={attribute}
                attributeSourceMapping={attributeSourceMapping}
                loading={loading}
              />
            )}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        '&:hover .edit-icon-button': {
          opacity: 1,
          visibility: 'visible',
        },
      }}
    >
      <Tooltip
        title={
          typeof formattedDisplayValue !== 'object' && isOverflowed
            ? formattedDisplayValue
            : ''
        }
      >
        <ValueCellAttributeValueDisplay
          formattedDisplayValue={formattedDisplayValue}
          textRef={textRef}
          matchedOverride={matchedOverride}
          includeOverrideTooltip
        />
      </Tooltip>
      {/* Show ONLY CalculationIcon if derived */}
      {showCalculationIcon && (
        <IconButton
          className="edit-icon-button disabled-svg-icon"
          onClick={handleClickAttribute}
          color="primary"
          title={
            !attribute?.show_calculation_popUp
              ? 'Enable the calculation popup to calculate this attribute.'
              : `Calculate ${attribute?.label}`
          }
          disabled={!attribute?.show_calculation_popUp}
          sx={{
            flexShrink: 0,
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
          }}
        >
          <CalculationIcon />
        </IconButton>
      )}

      {/* Show edit icon only if NOT derived */}
      {isEditEnabled && (
        <IconButton
          className="edit-icon-button"
          onClick={onEdit}
          size="small"
          color="primary"
          sx={{
            flexShrink: 0,
            opacity: 0,
            visibility: 'hidden',
            transition: 'opacity 0.2s ease-in-out, visibility 0.2s ease-in-out',
          }}
        >
          <ModeEditOutlinedIcon />
        </IconButton>
      )}

      {hideAttributeInfo && (
        <InfoIconButton
          attribute={attribute}
          attributeSourceMapping={attributeSourceMapping}
          loading={loading}
        />
      )}
    </Box>
  );
};

export default ValueCell;
